// Import global styles
import './styles/global.css';

// Import React for JSX
import React from 'react';

// Runtime image-host rewrite: germanos.gr → cloud.germanos.gr (app-wide)
import { installCloudImageRewrite } from './lib/cloudImageRewrite';

// GTM click bridge: fire the legacy data-click-event contract on isolated DOM
// contexts (shadow-DOM + iframe CMS pages) that GTM's native listener can't reach.
import { installGtmClickBridge } from './lib/gtmClickBridge';

// Discards a logged-out visitor's stale cart before the app reads localStorage.
import { purgeExpiredGuestCart } from './lib/guest-cart-expiry';

// Import Staging Preview feature — provider, banner, fetch + XHR interceptors
import {
  installSlotBootstrap,
  installStagingFetchInterceptor,
  installXhrInterceptor,
} from './features/staging-preview';

// Suppress console warnings for socket.io WebSocket connection errors (Gatsby HMR uses socket.io internally)
// These are harmless connection attempts that fail when HMR isn't fully initialized
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const originalError = console.error;
  const originalWarn = console.warn;

  // Filter console.error
  console.error = (...args) => {
    const message = args.join(' ');
    if (
      (message.includes('socket.io') && message.includes('websocket')) ||
      (message.includes('ws://localhost:8000/socket.io'))
    ) {
      // Suppress socket.io connection errors - they're from Gatsby's HMR and are harmless
      return;
    }
    originalError.apply(console, args);
  };

  // Filter console.warn for page-data.json preload warnings and informational messages
  console.warn = (...args) => {
    const message = args.join(' ');
    if (
      message.includes('preloaded with link preload was not used') ||
      message.includes('page-data.json') ||
      message.includes('preload tag') ||
      message.includes('Download the React DevTools') ||
      message.includes('loading indicator is displayed') ||
      message.includes('Query On Demand')
    ) {
      // Suppress informational warnings and page-data.json preload warnings
      return;
    }
    originalWarn.apply(console, args);
  };

  // Filter console.info for HMR and Gatsby informational messages
  const originalInfo = console.info;
  console.info = (...args) => {
    const message = args.join(' ');
    if (
      message.includes('[HMR]') ||
      message.includes('gatsby') ||
      message.includes('loading indicator')
    ) {
      // Suppress HMR and Gatsby informational messages
      return;
    }
    originalInfo.apply(console, args);
  };
}

// Global fetch interceptor: route relative /api requests to API domain
export const onClientEntry = () => {
  // Block Chrome/Android "Install app" PWA prompt — we ship the native G-App instead.
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
    });
  }

  // Rewrite any germanos.gr image host → cloud.germanos.gr CDN, app-wide.
  installCloudImageRewrite();

  // Bridge legacy data-click-event GTM tracking to shadow-DOM + iframe CMS pages
  // (light-DOM pages keep firing via GTM's native listener — the bridge skips them).
  installGtmClickBridge();

  // Read from window global set in gatsby-ssr.js, default to localhost:4002 for development (grm-bff)
  const DEFAULT_LOCAL_API_BASE = 'http://localhost:4002';
  let API_BASE = DEFAULT_LOCAL_API_BASE; // Default for development (grm-bff microservice)

  const isLoopbackHost = (host) => {
    try {
      const hostname = new URL(`http://${host}`).hostname.replace(/^\[|\]$/g, '');
      return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    } catch (_) {
      return false;
    }
  };

  if (typeof window !== 'undefined') {
    if (window.__API_BASE__ && window.__API_BASE__ !== '__GATSBY_CLIENT_TARGET__') {
      API_BASE = window.__API_BASE__;
    }
    try {
      if (
        isLoopbackHost(window.location.host)
        && isLoopbackHost(new URL(API_BASE).host)
        && new URL(API_BASE).host === window.location.host
      ) {
        API_BASE = DEFAULT_LOCAL_API_BASE;
        window.__API_BASE__ = DEFAULT_LOCAL_API_BASE;
      }
    } catch (_) {
      // ignore invalid API_BASE URL
    }
  }

  // Preview API routing: when the storefront is served from the internal AKS UI
  // host (only the admin staging-preview iframe loads it there), route /api to the
  // internal grm-bff origin instead of public cloud.germanos.gr. That origin has
  // no Apache cache (=> fresh staging/slot content) and grm-bff returns CORS for
  // this origin (=> no preflight 403). Public traffic on cloud.germanos.gr is
  // unaffected: window.location.host won't equal __PREVIEW_HOST__.
  if (typeof window !== 'undefined'
      && window.__PREVIEW_HOST__ && window.__PREVIEW_API_BASE__
      && window.__PREVIEW_API_BASE__ !== '__GATSBY_PREVIEW_API_TARGET__'
      && window.location.host === window.__PREVIEW_HOST__) {
    API_BASE = window.__PREVIEW_API_BASE__;
    // Also re-point the global so the direct readers of window.__API_BASE__
    // (client.ts, lib/cart-api.ts, pages/cart/api.ts) that build ABSOLUTE API
    // URLs — and thus bypass the relative-only fetch interceptor below — hit the
    // internal grm-bff origin too. Without this they would still call
    // cloud.germanos.gr cross-origin in preview (CORS 403 / stale).
    window.__API_BASE__ = window.__PREVIEW_API_BASE__;
  }

  // Same-origin API for alternate public hosts (e.g. bo.germanos.gr backoffice).
  // When the page is served from a host other than the baked GATSBY_CLIENT_TARGET
  // host, route /api to window.location.origin so Apache can proxy to grm-bff
  // without cross-origin CORS or cookie-scope mismatches.
  if (typeof window !== 'undefined' && API_BASE) {
    try {
      const bakedHost = new URL(API_BASE).host;
      const previewHost = window.__PREVIEW_HOST__;
      const isLocalDevBridge = isLoopbackHost(window.location.host) && isLoopbackHost(bakedHost);
      if (
        window.location.host !== bakedHost
        && window.location.host !== previewHost
        && !isLocalDevBridge
        && window.location.protocol.startsWith('http')
      ) {
        API_BASE = window.location.origin;
        window.__API_BASE__ = API_BASE;
      }
    } catch (_) {
      // ignore invalid API_BASE URL
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Global "page is being scrolled" beacon
  // ─────────────────────────────────────────────────────────────────────
  //
  // While the user is actively scrolling the page, sets the attribute
  // `data-page-scrolling="true"` on <html>. Clears it 120 ms after the last
  // scroll event (= scrolling has stopped).
  //
  // ── Why this beacon exists ──
  //
  // A long-standing visual bug on the homepage and other long-scroll pages:
  // many section components (`TrendingCategories`, `Carousel`, `BlogPostsSlider`,
  // `HeroBanner`, `BlogSection`, `CategoryGrid`, etc.) carry hover-driven
  // transforms (`hover:scale-*`, `hover:-translate-y-*`) plus a `transition-all`
  // shorthand. When the user scrolls vertically, the cursor stays pinned at a
  // fixed viewport position while the page content slides past it. The cursor
  // therefore enters/leaves the bounding box of every element it passes — each
  // entry fires `:hover`, each exit fires `:hover` off, and every transition
  // animates over its full duration (200–500 ms). The compounded effect is the
  // whole page visibly "trembling" during scroll.
  //
  // The beacon below lets a single global CSS rule (in styles/index.css) gate
  // hover effects on opted-in containers. Containers mark themselves with
  // `data-page-scroll-quiet` (see HomePage.tsx). The rule sets
  // `pointer-events: none` on those containers while the beacon is set, which
  // prevents `:hover` from firing at all during scroll. Static layout styles
  // and hover-independent transitions (e.g. the WhiteRibbon's grid-template-rows
  // collapse, modal fades, the header — all OUTSIDE the quiet container) are
  // untouched.
  //
  // ── Why the listener lives here, not in a React component ──
  //
  // 1. `onClientEntry` runs exactly once per session, before any page mounts —
  //    no double-install race during HMR or route changes.
  // 2. Putting it inside a useEffect would mount one listener per consumer
  //    component; on a 30-card homepage that is 30 listeners doing the same
  //    work. Module-level singleton is cheaper and simpler.
  // 3. The listener is `passive: true` so it never blocks the scroll thread.
  //
  // ── Idle timeout ──
  //
  // 120 ms is short enough that a click immediately after scrolling (~150 ms
  // typical reaction time) lands AFTER the beacon clears, so pointer-events
  // are restored before the click is dispatched. It is long enough that
  // momentum-scroll micro-pauses on iOS / trackpad don't prematurely clear
  // the beacon mid-scroll.
  if (typeof window !== 'undefined') {
    let scrollEndTimer = null;
    const onPageScroll = () => {
      document.documentElement.dataset.pageScrolling = 'true';
      if (scrollEndTimer !== null) {
        window.clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = window.setTimeout(() => {
        delete document.documentElement.dataset.pageScrolling;
        scrollEndTimer = null;
      }, 120);
    };
    window.addEventListener('scroll', onPageScroll, { passive: true });
  }

  if (typeof window !== 'undefined' && API_BASE) {
    const originalFetch = window.fetch.bind(window);

    // Customer auth is now handled via HttpOnly cookies.
    // Admin/dev auth token (userAuthToken) is still handled manually.
    const addAuthHeader = (url, init) => {
      // Skip for backoffice requests — they use HttpOnly cookies
      if (url.startsWith('/api/backoffice')) {
        return init;
      }
      // Admin/dev auth: check for userAuthToken (non-Cosmote-ID dev auth)
      try {
        const devToken = localStorage.getItem('userAuthToken');
        if (devToken) {
          const existingHeaders = init?.headers || {};
          const hasAuth = Object.keys(existingHeaders).some(
            key => key.toLowerCase() === 'authorization'
          );
          if (!hasAuth) {
            return { ...init, headers: { ...existingHeaders, 'Authorization': `Bearer ${devToken}` } };
          }
        }
      } catch (_) {
        // localStorage not available (SSR)
      }
      return init;
    };

    // Ensure credentials: 'include' for ALL /api/ requests
    // so HttpOnly cookies (customer_access_token, bo_access_token) are sent.
    const ensureCredentials = (url, init) => {
      if (url.startsWith('/api/')) {
        return { ...init, credentials: 'include' };
      }
      return init;
    };

    window.fetch = (input, init) => {
      try {
        const isString = typeof input === 'string';
        const url = isString ? input : input.url;
        // Only rewrite relative paths. Leave absolute http(s) as-is.
        const isRelative = url.startsWith('/');
        if (isRelative) {
          // If path starts with /api/, route to BFF (includes /api/backoffice/* naturally)
          if (url.startsWith('/api/')) {
            const newUrl = API_BASE.replace(/\/$/, '') + url;
            let newInit = addAuthHeader(url, init); // Add customer JWT token (skip for backoffice)
            newInit = ensureCredentials(url, newInit); // Add credentials for backoffice cookies
            if (isString) {
              return originalFetch(newUrl, newInit);
            }
            const req = new Request(newUrl, input);
            return originalFetch(req, newInit);
          }
          // Otherwise, check if it needs /api prefix added
          const needsApiPrefix = ['/cms', '/catalog', '/import', '/media', '/badges', '/products', '/trending-categories']
            .some(p => url.startsWith(p + '/') || url === p);
          if (needsApiPrefix) {
            const newUrl = API_BASE.replace(/\/$/, '') + '/api' + url;
            const newInit = addAuthHeader(url, init); // Add customer JWT token
            if (isString) {
              return originalFetch(newUrl, newInit);
            }
            const req = new Request(newUrl, input);
            return originalFetch(req, newInit);
          }
        }
      } catch (_) {
        // fall through to original fetch
      }
      return originalFetch(input, init);
    };

    // n-projects slot preview: if the URL carries ?slot=N, persist it in
    // sessionStorage so the fetch/XHR interceptors below stamp X-Project-Slot
    // on every API call. Must run BEFORE the fetch interceptor installs so a
    // request that fires immediately after entry already carries the header.
    installSlotBootstrap();

    // Staging preview: wraps the URL-rewriting fetch above. When sessionStorage
    // holds a staging cycle id OR a slot (n-projects), stamps the routing
    // headers on every /api/ call. On 410 (invalid cycle) flushes session and
    // bounces to /.
    installStagingFetchInterceptor();
    // XHR counterpart — most storefront code uses fetch, but legacy analytics /
    // tracking libraries issue XHRs that would otherwise hit live data mid-preview.
    installXhrInterceptor();
  }

  // Drop a logged-out visitor's abandoned cart. Runs here because `onClientEntry`
  // is the last hook before the app bundle evaluates `hooks/useCart.ts`, whose
  // module-scope hydrate() reads localStorage synchronously — purging any later
  // would paint the expired items in the header badge before removing them.
  // Placed after the API_BASE resolution and the interceptors so its settings
  // lookup targets the right origin and carries the staging/slot headers.
  purgeExpiredGuestCart();
};

// SEO redirect checks are handled exclusively by CmsPageResolver (catch-all route)
// to avoid race conditions between multiple async redirect systems.

// Wrap the root element with providers. Shared with gatsby-ssr.js so the
// browser and server trees can never drift apart.
export { wrapRootElement } from './wrap-root-element';
